import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { InputTypes } from "./UserAcessListComponent.types";
import { BusyIndicator } from "../../../shared/base-components/react-loader/busy-indicator/BusyIndicator";
import { Overlay } from "../../../shared/base-components/react-overlay/Overlay";
import { Select } from "../../../shared/base-components/react-select/Select";
import UserAccessService from "../../../features/UserAccessView/services/UserAccessService";
import {
  ACCESS_TYPE,
  ACTIVE_TABS_KEY,
  NO_PROJECTS_FOUND_PLACEHOLDER,
  TOAST_DEFAULT_ERROR_MESSAGE,
  USER_ACCESS,
  USER_NAME_LABEL,
  WRITE_ACCESS,
} from "../../utils/CommonConstants";

import { PropelCard } from "../../../styles/PropelCard/PropelCard.styles";
import {
  Border,
  ButtonContainer,
  Close,
  FlexBlock,
  InviteButtonContainer,
  InviteUserContainer,
  InviteUserList,
  Label,
  SearchIcon,
  SelectWrapper,
  UserList,
  UserListOutline,
  UserListWrapper,
  EmptyProjectPlaceholderContainer,
  EmptyProjectPlaceholderContent,
  FlexContainer,
  LoaderWrapperComponent,
} from "./UserAccessListComponent.styles";
import { UserAccess } from "./UserAcessListComponent.types";
import { Button, Checkbox, message } from "antd";
import { Container } from "../../../shared/base-components/Layouts/Container/Container.styles";

export const UserAccessList = () => {
  const [isEverythingLoaded, setIsEverythingLoaded] = useState(false);
  const [userAccessData, setUserAccessData] = useState<UserAccess[]>([]);
  const [userWithoutAccess, setUserWithoutAccess] = useState<UserAccess[]>([]);
  const [showOverlay, setShowOverlay] = useState(false);
  const [selectedUser, setSelectedUser] = useState("");
  const [userList, setUserList] = useState<UserAccess[]>([]);
  const [checkedItems, setCheckedItems] = useState<{ [key: number]: boolean }>(
    {}
  );
  const navigate = useNavigate();
  const { deliverableId } = useParams<{ deliverableId: string }>();
  const projectService = new UserAccessService();

  useEffect(() => {
    fetchUserAccessData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchUserAccessData = async (): Promise<void> => {
    try {
      const response = await projectService.getUserDetailsById<UserAccess[]>(
        Number(deliverableId)
      );
      const sortedResponse = sortOwnerOnTop(response);
      setUserAccessData(sortedResponse);
      setIsEverythingLoaded(true);
    } catch (error) {
      message.error(TOAST_DEFAULT_ERROR_MESSAGE);
    }
  };

  const fetchUserWithoutAccess = async (): Promise<void> => {
    try {
      const response = await projectService.getUserWithoutAccess<UserAccess[]>(
        Number(deliverableId)
      );
      const sortedResponse = sortOwnerOnTop(response);
      setUserWithoutAccess(sortedResponse);
      setIsEverythingLoaded(true);
    } catch (error) {
      message.error(TOAST_DEFAULT_ERROR_MESSAGE);
    }
  };

  // Reorder the userAccessData array so that the owner comes first
  const sortOwnerOnTop = (data: UserAccess[]) => {
    const ownerIndex = data.findIndex((access) => access.isOwner);
    if (ownerIndex !== -1) {
      const owner = data[ownerIndex];
      const modifiedData = data.filter((_, index) => index !== ownerIndex);
      return [owner, ...modifiedData];
    }
    return data;
  };

  const handleCheckboxChange = (index: number): void => {
    setCheckedItems((prevCheckedItems) => ({
      ...prevCheckedItems,
      [index]: !prevCheckedItems[index],
    }));

    setUserList((prevUserAccessData) => {
      return prevUserAccessData.map((access, idx) => {
        if (idx === index) {
          return {
            ...access,
            canWrite: !access.canWrite,
            canRead: access.canWrite,
          };
        }
        return access;
      });
    });
  };

  const handleSelectUser = (selectedUser: string): void => {
    const userToAdd = userWithoutAccess.find(
      (user) => user.userName === selectedUser
    );

    if (userToAdd) {
      const newUser: UserAccess = {
        ...userToAdd,
        canRead: true,
      };
      setUserList([...userList, newUser]);
      setUserWithoutAccess((prevUserAccessData) =>
        prevUserAccessData.filter((user) => user.userName !== selectedUser)
      );
    }
  };

  const handleRemoveUser = (index: number): void => {
    if (index < 0 || index >= userList.length) {
      return;
    }

    const removedUser = userList[index];
    setUserList(userList.filter((user, idx) => idx !== index));
    setUserWithoutAccess((prevUserAccessData) => [
      ...prevUserAccessData,
      removedUser,
    ]);
  };

  const handleInviteButton = (): void => {
    setShowOverlay(true);
    fetchUserWithoutAccess();
  };

  const handleInvite = async (): Promise<void> => {
    try {
      if (deliverableId) {
        const deliverableIdNumber = Number(deliverableId);
        const responseData = await projectService.updateUserWithoutAccess<
          string[]
        >(deliverableIdNumber, userList);
        message.success(responseData);
      }
    } catch (error) {
      message.error(TOAST_DEFAULT_ERROR_MESSAGE);
    }
    setShowOverlay(false);
    setUserList([]);
    fetchUserAccessData();
  };

  const handleCloseOverlay = () => {
    setShowOverlay(false);
    setUserList([]);
  };

  const getAccessType = (access: UserAccess): string => {
    if (access.isOwner) {
      return "Owner";
    } else if (access.canWrite) {
      return "Write";
    } else if (access.canRead) {
      return "Read";
    } else {
      return "No Access";
    }
  };

  return (
    <>
      {isEverythingLoaded ? (
        <>
          <Container>
            <Label data-test-id="user-access-label">
              <li className="user">{USER_NAME_LABEL}</li>
              <li>{ACCESS_TYPE}</li>
              <li>{WRITE_ACCESS}</li>
            </Label>
            <UserListWrapper>
              <UserListOutline data-test-id="user-access-list">
                {userAccessData?.length ? (
                  userAccessData.map((access) => (
                    <PropelCard key={access.id}>
                      <FlexContainer>
                        <FlexBlock>{access.userName}</FlexBlock>{" "}
                        <FlexBlock>
                          <span>{getAccessType(access)}</span>
                        </FlexBlock>
                        <FlexBlock>
                          <Checkbox
                            value={access.isOwner || access.canWrite}
                            onChange={() => true}
                          />
                        </FlexBlock>
                      </FlexContainer>
                    </PropelCard>
                  ))
                ) : (
                  <EmptyProjectPlaceholderContainer data-testid="empty-project-placeholder">
                    <EmptyProjectPlaceholderContent>
                      <div>{NO_PROJECTS_FOUND_PLACEHOLDER}</div>
                    </EmptyProjectPlaceholderContent>
                  </EmptyProjectPlaceholderContainer>
                )}
              </UserListOutline>
            </UserListWrapper>
          </Container>
          <ButtonContainer>
            <Button
              type="default"
              onClick={() =>
                navigate(
                  `/deliverable/user-access/${deliverableId}/edit?${ACTIVE_TABS_KEY}=${USER_ACCESS}`
                )
              }
            >
              Edit
            </Button>
            <Button
              data-test-id="invite-button"
              type="primary"
              onClick={handleInviteButton}
            >
              Invite
            </Button>
          </ButtonContainer>
        </>
      ) : (
        <LoaderWrapperComponent>
          <BusyIndicator />
        </LoaderWrapperComponent>
      )}
      {/* Overlay component */}
      <Overlay data-test-id="overlay" isVisible={showOverlay} fullWidth={true}>
        <InviteUserContainer data-testid="invite-user-popup">
          <SelectWrapper>
            <SearchIcon src="/search.svg" alt="Search Icon" />
            <Select
              options={userWithoutAccess.map((user) => user.userName)}
              noOptionsFoundLabel="Options not found"
              placeHolder="Search"
              type={InputTypes.select}
              onInputValueChange={(selectedUser) => {
                setSelectedUser(selectedUser);
                handleSelectUser(selectedUser);
              }}
            />
          </SelectWrapper>

          <InviteUserList>
            <Label>
              <li className="user">{USER_NAME_LABEL}</li>
              <li>{WRITE_ACCESS}</li>
            </Label>
            <Border />
            <UserList data-testid="user-list">
              {userList?.length > 0 &&
                userList.map((access, index) => (
                  <PropelCard key={access.id} showClose={true}>
                    <Close
                      data-testid="close-button"
                      onClick={() => handleRemoveUser(index)}
                    >
                      X
                    </Close>
                    <FlexContainer>
                      <FlexBlock>{access.userName}</FlexBlock>{" "}
                      <FlexBlock>
                        <div>
                          <Checkbox
                            value={checkedItems[index]}
                            onChange={() => handleCheckboxChange(index)}
                          />
                        </div>
                      </FlexBlock>
                    </FlexContainer>
                  </PropelCard>
                ))}
            </UserList>
          </InviteUserList>

          <InviteButtonContainer>
            <Button id="cancel-user-access" type="default" onClick={handleCloseOverlay}>
              Cancel
            </Button>
            <Button
              id="invite-user-access"
              type="primary"
              onClick={handleInvite}
              disabled={!selectedUser || userList.length === 0}
            >
              Invite
            </Button>
          </InviteButtonContainer>
        </InviteUserContainer>
      </Overlay>
    </>
  );
};
